import {
  PrimaryControlsStripToolbarHeightContext,
  type PrimaryControlsStripToolbarSurface,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.toolbar-height";
import { useContext, useSyncExternalStore } from "react";

/**
 * The height of the opaque toolbar row the FOCUSED screen is currently rendering, or `0` when it
 * renders none. `NavLayout` offsets the shared header fade by this so the gradient is fully opaque
 * exactly one pixel under the toolbar buttons' bottom border.
 */
export function usePrimaryControlsStripToolbarHeight(): number {
  const registry = useContext(PrimaryControlsStripToolbarHeightContext);
  return useSyncExternalStore(registry.subscribe, registry.getCurrent, registry.getCurrent);
}

/**
 * What the FOCUSED screen's toolbar row paints. The shell's status band and the header fade use
 * it, so the top bar is one surface by construction rather than by a route list.
 */
export function usePrimaryControlsStripToolbarSurface(): PrimaryControlsStripToolbarSurface {
  const registry = useContext(PrimaryControlsStripToolbarHeightContext);
  return useSyncExternalStore(registry.subscribe, registry.getSurface, registry.getSurface);
}

/** Whether the FOCUSED screen renders a `PrimaryControlsStripToolbar` at all. */
export function useHasPrimaryControlsStripToolbar(): boolean {
  const registry = useContext(PrimaryControlsStripToolbarHeightContext);
  return useSyncExternalStore(registry.subscribe, registry.hasToolbar, registry.hasToolbar);
}

/**
 * Whether the FOCUSED screen's top bar floats as glass over its page scroller (iOS 26). The shell
 * then leaves the status band and the header fade to UIKit's scroll edge effect.
 */
export function usePrimaryControlsStripToolbarFloating(): boolean {
  const registry = useContext(PrimaryControlsStripToolbarHeightContext);
  return useSyncExternalStore(registry.subscribe, registry.isFloating, registry.isFloating);
}
